import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Badge } from "reactstrap";
import QwikForm from "components/form/QwikForm";
import QwikInput from "components/form/QwikInput";
import { useGetAllJobsQuery, useGetJobQuery } from "store/features/jobs/jobManagementApi";
import { useCreateJobApplicationMutation } from "store/features/jobs/jobApplicationApi";
import { toast } from "sonner";

const JobApplyPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [selectedJobId, setSelectedJobId] = useState(jobId || "");
  const [cvFile, setCvFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const { data: jobs = [], isLoading: jobsLoading } = useGetAllJobsQuery();
  const { data: jobDetail, isLoading: jobLoading } = useGetJobQuery(selectedJobId, {
    skip: !selectedJobId,
  });
  const [createJobApplication, { isLoading: submitting }] = useCreateJobApplicationMutation();

  useEffect(() => {
    if (jobId) {
      setSelectedJobId(jobId);
    }
  }, [jobId]);

  const jobOptions = [
    { value: "", label: "-- Select a Job Circular --" },
    ...jobs.map(j => ({
      value: String(j.id),
      label: `${j.title} - ${j.company || 'QwikIT'} (${j.location || 'Remote'})`,
    })),
  ];

  const activeJob = jobDetail || jobs.find(j => String(j.id) === String(selectedJobId));

  const handleSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append("applicantname", data.applicantname || "");
      formData.append("applicantemail", data.applicantemail || "");
      formData.append("contactNumber", data.contactNumber || "");
      formData.append("applicantexperience", data.applicantexperience || "0");
      
      const targetJobId = selectedJobId || data.jobid;
      if (targetJobId) {
        formData.append("jobid", targetJobId);
      }
      formData.append("title", activeJob ? activeJob.title : (data.title || ""));
      formData.append("currentSalary", data.currentSalary || "");
      formData.append("expectedSalary", data.expectedSalary || "");
      formData.append("noticePeriod", data.noticePeriod || "");
      formData.append("country", data.country || "Bangladesh");
      const todayDate = new Date().toISOString().split('T')[0];
      formData.append("date", todayDate);
      formData.append("applystatus", true);
      formData.append("selectionstatus", false);

      if (cvFile) {
        formData.append("cv", cvFile);
      }

      await createJobApplication(formData).unwrap();
      toast.success("Application submitted successfully!");
      setSubmitted(true);
    } catch (error) {
      console.error("Job Application Submission Error:", error);
      const errMsg = error?.data?.detail || error?.data?.message || "Failed to submit application. Please check form details.";
      toast.error(errMsg);
    }
  };

  return (
    <div className="page-content py-5 bg-light" style={{ minHeight: "100vh" }}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={10}>
            {/* Header Banner */}
            <div className="text-center mb-4">
              <h2 className="fw-bold text-primary mb-2">Job Application Portal</h2>
              <p className="text-muted fs-6">Submit your application to join our growing team</p>
            </div>

            {submitted ? (
              <Card className="shadow-sm border-0 text-center py-5">
                <CardBody>
                  <div className="avatar-lg mx-auto mb-4 bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center" style={{ width: "80px", height: "80px" }}>
                    <i className="fas fa-check-circle fa-3x"></i>
                  </div>
                  <h3 className="fw-bold text-success mb-2">Thank You for Applying!</h3>
                  <p className="text-muted mb-4 fs-6">
                    Your application for <strong>{activeJob?.title || "the position"}</strong> has been received successfully.
                    Our HR team will review your application and contact you soon.
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <Button color="primary" onClick={() => { setSubmitted(false); setCvFile(null); }}>
                      Submit Another Application
                    </Button>
                    <Link to="/all-jobs" className="btn btn-outline-secondary">
                      View All Jobs
                    </Link>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <Row>
                {/* Left Column: Job Details */}
                <Col lg={5} className="mb-4 mb-lg-0">
                  <Card className="shadow-sm border-0 h-100">
                    <CardHeader className="bg-primary text-white py-3">
                      <h5 className="card-title text-white mb-0">
                        <i className="fas fa-briefcase me-2"></i>Job Overview
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Select Job Circular</label>
                        <select
                          className="form-select form-select-lg"
                          value={selectedJobId}
                          onChange={(e) => setSelectedJobId(e.target.value)}
                        >
                          {jobOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {jobLoading ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading job details...</span>
                          </div>
                        </div>
                      ) : activeJob ? (
                        <div>
                          <hr />
                          <h4 className="fw-bold text-primary mb-1">{activeJob.title}</h4>
                          <h6 className="text-muted mb-3">{activeJob.company || "QwikIT"} - {activeJob.location || "Remote"}</h6>

                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {activeJob.position && (
                              <Badge color="info" className="px-2 py-1 fs-6">{activeJob.position}</Badge>
                            )}
                            {activeJob.jobcatagory && (
                              <Badge color="secondary" className="px-2 py-1 fs-6">{activeJob.jobcatagory}</Badge>
                            )}
                            {activeJob.salary && (
                              <Badge color="success" className="px-2 py-1 fs-6">💰 {activeJob.salary}</Badge>
                            )}
                          </div>

                          {activeJob.aboutpossitiondetails && (
                            <div className="mb-3">
                              <h6 className="fw-bold text-dark mb-1">About Position:</h6>
                              <p className="text-secondary small">{activeJob.aboutpossitiondetails}</p>
                            </div>
                          )}

                          {activeJob.requirementsdetails && (
                            <div className="mb-3">
                              <h6 className="fw-bold text-dark mb-1">Requirements:</h6>
                              <p className="text-secondary small">{activeJob.requirementsdetails}</p>
                            </div>
                          )}

                          {activeJob.responsibilites && (
                            <div className="mb-3">
                              <h6 className="fw-bold text-dark mb-1">Responsibilities:</h6>
                              <p className="text-secondary small">{activeJob.responsibilites}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="alert alert-info mt-3">
                          Please select a job circular from the dropdown above to view job details and apply.
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* Right Column: Application Form */}
                <Col lg={7}>
                  <Card className="shadow-sm border-0">
                    <CardHeader className="bg-white py-3">
                      <h5 className="card-title text-dark mb-0">
                        <i className="fas fa-paper-plane me-2 text-primary"></i>Candidate Application Form
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <QwikForm onSubmit={handleSubmit}>
                        <Row>
                          <Col lg={12}>
                            <div className="mb-3">
                              <QwikInput
                                name="applicantname"
                                label="Full Name *"
                                placeholder="Enter your full name"
                                type="text"
                                className="form-control"
                                required
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <QwikInput
                                name="applicantemail"
                                label="Email Address *"
                                placeholder="name@example.com"
                                type="email"
                                className="form-control"
                                required
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <QwikInput
                                name="contactNumber"
                                label="Phone / WhatsApp *"
                                placeholder="017XXXXXXXX"
                                type="text"
                                className="form-control"
                                required
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <QwikInput
                                name="applicantexperience"
                                label="Total Experience (Years)"
                                placeholder="e.g. 2"
                                type="number"
                                className="form-control"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <QwikInput
                                name="country"
                                label="Country"
                                placeholder="e.g. Bangladesh"
                                type="text"
                                className="form-control"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <QwikInput
                                name="currentSalary"
                                label="Current Salary (BDT)"
                                placeholder="e.g. 35000"
                                type="text"
                                className="form-control"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <QwikInput
                                name="expectedSalary"
                                label="Expected Salary (BDT)"
                                placeholder="e.g. 50000"
                                type="text"
                                className="form-control"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <QwikInput
                                name="noticePeriod"
                                label="Notice Period"
                                placeholder="e.g. 1 Month / Immediate"
                                type="text"
                                className="form-control"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="mb-3">
                              <label className="form-label">Resume / CV File (PDF, DOCX)</label>
                              <input
                                type="file"
                                className="form-control"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => setCvFile(e.target.files[0])}
                              />
                              <small className="text-muted">Optional: Upload your CV file</small>
                            </div>
                          </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <Link to="/all-jobs" className="btn btn-light px-4">
                            Cancel
                          </Link>
                          <Button type="submit" color="primary" className="px-4" disabled={submitting}>
                            {submitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-paper-plane me-2"></i>
                                Submit Application
                              </>
                            )}
                          </Button>
                        </div>
                      </QwikForm>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default JobApplyPage;
